import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';

export default function CallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for OAuth errors in the URL hash
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const errorParam = params.get('error');
      const errorDescription = params.get('error_description');
      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }
    }

    // Wait for session to be ready before navigating
    let cancelled = false;

    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (cancelled) return;

        if (sessionError) {
          setError(sessionError.message);
          return;
        }

        if (session) {
          navigate('/dashboard');
          return;
        }

        // No session yet — listen for auth state change
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
          if (cancelled) return;
          if (session) {
            navigate('/dashboard');
            subscription.unsubscribe();
          }
        });

        // Fallback timeout after 10 seconds
        const fallbackTimer = setTimeout(() => {
          if (!cancelled) {
            subscription.unsubscribe();
            setError('Login timed out. Please try again.');
          }
        }, 10000);

        // Store cleanup references
        return () => {
          cancelled = true;
          subscription.unsubscribe();
          clearTimeout(fallbackTimer);
        };
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        }
      }
    };

    const cleanupPromise = checkSession();

    return () => {
      cancelled = true;
      // Clean up the subscription if checkSession set one up
      cleanupPromise?.then((cleanup) => cleanup?.());
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-soft-light-gray">
        <div className="text-center max-w-md px-4">
          <div className="text-red-500 text-4xl mb-4">!</div>
          <h2 className="text-lg font-semibold text-slate-gray mb-2">Authentication Error</h2>
          <p className="text-sm text-slate-gray mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-hero-blue hover:text-hero-blue/80 underline"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-soft-light-gray">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-blue mx-auto mb-4"></div>
        <p className="text-slate-gray">Logging you in...</p>
      </div>
    </div>
  );
}
